/**
 * Horizontal Bar Chart Renderer
 *
 * Creates an amCharts 5 XYChart with horizontal ColumnSeries.
 * Supports conditional coloring (green/red) based on a threshold value,
 * commonly used for YoY change charts.
 */

import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

import type { HorizontalBarConfig } from '../../../../types/dashboard';
import { DASHBOARD_TOKENS } from '../tokens';
import { buildAmChartsNumberFormat } from '../formatters';

export function buildHorizontalBarChart(
  root: am5.Root,
  config: HorizontalBarConfig,
  data: Record<string, unknown>[]
): am5xy.XYChart {
  const chart = root.container.children.push(
    am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: 'none',
      wheelY: 'none',
      layout: root.verticalLayout,
    })
  );

  // Category axis on Y (horizontal bars)
  const yRenderer = am5xy.AxisRendererY.new(root, {
    inversed: config.axes?.y?.inversed !== false,
    cellStartLocation: 0.1,
    cellEndLocation: 0.9,
    minGridDistance: 20,
  });
  yRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSize,
    fill: am5.color(DASHBOARD_TOKENS.textSecondary),
    maxWidth: 150,
    oversizedBehavior: 'truncate',
  });

  const yAxis = chart.yAxes.push(
    am5xy.CategoryAxis.new(root, {
      categoryField: config.categoryField,
      renderer: yRenderer,
    })
  );

  // Value axis on X
  const xRenderer = am5xy.AxisRendererX.new(root, {});
  xRenderer.labels.template.setAll({
    fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    fill: am5.color(DASHBOARD_TOKENS.textMuted),
  });

  const numberFormat = buildAmChartsNumberFormat(config.axes?.x?.format, config.axes?.x?.currency);

  const xAxis = chart.xAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: xRenderer,
      min: config.axes?.x?.min,
      max: config.axes?.x?.max,
      numberFormat,
    })
  );

  const tooltipText = numberFormat
    ? `{categoryY}: {valueX.formatNumber("${numberFormat}")}`
    : '{categoryY}: {valueX}';

  config.series.forEach((seriesConfig, idx) => {
    const defaultColor = seriesConfig.color || DASHBOARD_TOKENS.palette[idx % DASHBOARD_TOKENS.palette.length];

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: seriesConfig.name,
        xAxis,
        yAxis,
        valueXField: seriesConfig.valueField,
        categoryYField: config.categoryField,
        fill: am5.color(defaultColor),
        stroke: am5.color(defaultColor),
      })
    );

    series.columns.template.setAll({
      cornerRadiusTR: config.visual?.cornerRadius ?? 3,
      cornerRadiusBR: config.visual?.cornerRadius ?? 3,
      strokeOpacity: 0,
      height: am5.percent(70),
      tooltipText: seriesConfig.tooltip || tooltipText,
      tooltipX: am5.percent(100),
      tooltipY: am5.percent(50),
    });

    // Conditional coloring: green for positive, red for negative
    if (config.conditionalColors) {
      const cc = config.conditionalColors;
      // amCharts5 narrows `target.dataItem.get(...)` to the keys it can statically
      // prove exist on the column-template data item, which excludes the runtime
      // `valueX` field present on every XY series data item. Cast to the real
      // shape rather than reaching for `any`.
      type XYDataItem = am5.DataItem<{ visible?: boolean; valueX?: number }>;
      series.columns.template.adapters.add('fill', (_fill, target) => {
        const dataItem = target.dataItem as XYDataItem | undefined;
        if (!dataItem) return am5.color(defaultColor);
        const val = dataItem.get('valueX') as number;
        if (val >= cc.threshold) return am5.color(cc.positive);
        return am5.color(cc.negative);
      });
      series.columns.template.adapters.add('stroke', (_stroke, target) => {
        const dataItem = target.dataItem as XYDataItem | undefined;
        if (!dataItem) return am5.color(defaultColor);
        const val = dataItem.get('valueX') as number;
        if (val >= cc.threshold) return am5.color(cc.positive);
        return am5.color(cc.negative);
      });
    }

    // Bullet labels at the end of bars
    if (config.bulletLabels?.show) {
      series.bullets.push(() => {
        return am5.Bullet.new(root, {
          locationX: 1,
          sprite: am5.Label.new(root, {
            text: '{valueX}',
            fill: am5.color(DASHBOARD_TOKENS.textPrimary),
            fontSize: DASHBOARD_TOKENS.fontSizeSmall,
            centerY: am5.percent(50),
            centerX: am5.percent(0),
            paddingLeft: 8,
            populateText: true,
          }),
        });
      });
    }

    series.data.setAll(data);
    series.appear();
  });

  yAxis.data.setAll(data);

  if (config.visual?.legend && config.series.length > 1) {
    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
      })
    );
    legend.labels.template.setAll({
      fill: am5.color(DASHBOARD_TOKENS.textSecondary),
      fontSize: DASHBOARD_TOKENS.fontSizeSmall,
    });
    legend.data.setAll(chart.series.values);
  }

  return chart;
}
